import React, { Fragment } from "react";
import Image from "@vorne/gatsby-theme-marketing/components/image/image";
import Page from "../../../components/page";
import { graphql, Script } from "gatsby";
import {
  Heading1,
  Body1,
  Heading2,
  Heading3,
} from "@vorne/gatsby-theme-marketing/components/typography/typography";
import BigIdea from "@vorne/gatsby-theme-marketing/components/bigidea/bigIdea";
import QuickLinks from "@vorne/gatsby-theme-marketing/components/quicklinks/quickLinks";
import PageTitle from "@vorne/gatsby-theme-marketing/components/pagetitle/pageTitle";
import List, {
  ListItem,
} from "@vorne/gatsby-theme-marketing/components/list/v1";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHeaderCell,
  TableCell,
} from "@vorne/gatsby-theme-marketing/components/table/table";
import Link from "@vorne/gatsby-theme-marketing/components/link/link";
import Routes from "../../../routes";
import Section from "@vorne/gatsby-theme-marketing/components/section/section";
import BannerAd from "@vorne/gatsby-theme-marketing/components/banner-ad/banner-ad";
import Center from "@vorne/gatsby-theme-marketing/components/layout/center/center";
import { Seo } from "@vorne/gatsby-theme-marketing/components/seo/v2";

const InformationDecisionAction = ({ data }) => {
  return (
    <Page>
      <Section>
        <PageTitle>IDA (Information, Decision, Action)</PageTitle>
        <BigIdea>
          Most companies place too much emphasis on results (looking backward)
          and too little emphasis on the factors that drive results: Information
          (the basis for effective decisions), Decision (making effective
          decisions), and Action (transforming decisions to results). Download
          the <Link url={Routes.Vorne.Files.IDAPDF}>IDA Model PDF</Link>.
        </BigIdea>
        <QuickLinks tag="h2" />
        <Heading1 id="whatIsIda">What Is IDA?</Heading1>
        <Body1>
          IDA is one of the easiest and most effective models for improving
          results in almost anything you do. How? Rather than spending time
          trying to manage results, which are by their very nature rooted in the
          past, IDA emphasizes the three factors that drive results:
        </Body1>
        <Table align="L L" cellWidths="15 85">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Factor</TableHeaderCell>
              <TableHeaderCell>Description</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell>Information</TableCell>
              <TableCell>
                Information is the foundation and starting point of IDA.
                Excellent information (i.e., accurate, relevant, and
                easy-to-understand) is a precondition for effective decision
                making.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Decision</TableCell>
              <TableCell>
                Decision is the pivot between Information and Action. It is the
                moment in time when Information is reviewed and a course of
                Action is decided. Decision is amplified through{" "}
                <Link url={Routes.Vorne.GreatMeetings}>Great Meetings</Link> and
                aligned across the business through{" "}
                <Link url={Routes.Vorne.StructuredImprovement}>
                  Structured Improvement
                </Link>
                .
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Action</TableCell>
              <TableCell>
                Action is where theoretical possibilities are transformed into
                tangible progress; where decisions are transformed into results.
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <Body1>
          <i>
            Note: IDA was developed through pioneering work by{" "}
            <Link url={Routes.Vorne.OptimumFX}>OptimumFX</Link>, who are
            UK-based thought leaders and experts on manufacturing improvement.
          </i>
        </Body1>
        <Heading1 id="whyUseTheIdaModel">Why Use the IDA Model?</Heading1>
        <Body1>
          IDA is simple enough to be understood by all employees and broad
          enough to serve as a framework for forwarding the goals of the
          business. By establishing behaviors that optimize the inputs
          (Information, Decision, and Action), the outputs (Results) will take
          care of themselves:
        </Body1>
        <List type="bullet">
          <ListItem>
            Generate accurate and relevant data that is understood by all
            stakeholders (<strong>Information</strong>)
          </ListItem>
          <ListItem>
            Establish standardized processes aligned across the business for
            making high-quality, timely, and effective decisions (
            <strong>Decision</strong>)
          </ListItem>
          <ListItem>
            Apply decisions quickly and consistently to create tangible progress
            (<strong>Action</strong>)
          </ListItem>
        </List>
        <Heading1 id="idaFramework">IDA Framework</Heading1>
        <Body1>
          The IDA framework includes all three factors (Information, Decision,
          and Action) and can be expressed as an equation (much like{" "}
          <Link url={Routes.Vorne.OverallEquipmentEffectiveness}>OEE</Link>):
        </Body1>
        <Center $horizontally>
          <Image
            src={data.idaEquationImage}
            alt="The IDA Model: Information times Decision times Action equals Results."
            renderCaption={() => (
              <Fragment>
                Like a chain, IDA is only as strong as its weakest link. All
                three factors (Information, Decision, and Action) must be
                present and in balance to achieve optimal results. If any factor
                is missing or suboptimal it is very difficult to achieve good
                results.
              </Fragment>
            )}
          />
        </Center>
        <Heading2>A Simple IDA Example</Heading2>
        <Body1>
          Imagine you are a pilot and it's your job to safely fly passengers
          from Chicago to London. In each of the following three scenarios there
          is an IDA problem that will prevent you from meeting your objective:
        </Body1>
        <List type="bullet">
          <ListItem>
            The gauges are inaccurate or broken (<strong>Information</strong>{" "}
            problem)
          </ListItem>
          <ListItem>
            The gauges are accurate but you don't use them (
            <strong>Decision</strong> problem)
          </ListItem>
          <ListItem>
            The gauges are accurate and you look at them; but you don't make any
            adjustments to the flight controls (<strong>Action</strong> problem)
          </ListItem>
        </List>
        <Body1>
          In other words, all three factors (Information, Decision, and Action)
          are necessary in order to achieve a good result.
        </Body1>
        <Heading2>Results Versus Objectives</Heading2>
        <Body1>
          It is interesting to think about the difference between results and
          objectives from the perspective of IDA.
        </Body1>
        <Body1>
          In the IDA model, <strong>results</strong> take care of themselves.
          Instead of obsessing about outputs (Results), the emphasis is on the
          inputs (Information, Decision and Action) that lead to great results.
        </Body1>
        <Body1>
          On the other hand, <strong>objectives</strong> are forward-looking and
          as such inform IDA. Specifically, given a set of objectives, work
          backwards to determine an optimal set of Information, Decision, and
          Actions. Applying IDA will reinforce the behaviors necessary to
          deliver the desired objectives.
        </Body1>
        <Heading1 id="benefitsOfTheIdaModel">
          Benefits of the IDA Model
        </Heading1>
        <Body1>
          In the <strong>short term</strong>, IDA is a simple framework for
          training employees to improve results by leveraging Information,
          Decision, and Actions.
        </Body1>
        <Body1>
          In the <strong>long term</strong>, IDA helps to achieve optimal
          results by ensuring all three factors (Information x Decision x
          Action) are present and balanced.
        </Body1>
        <Heading1 id="teamMemberRoles">Team Member Roles</Heading1>
        <Body1>IDA involves the following roles:</Body1>
        <Table align="L L" cellWidths="15 85">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Role</TableHeaderCell>
              <TableHeaderCell>Description</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell>Manager</TableCell>
              <TableCell>
                <List type="bullet">
                  <ListItem>
                    Uses rolled-up information at a site/department level.
                  </ListItem>
                  <ListItem>Plans with a scope of months to years.</ListItem>
                  <ListItem>Most actions are to improve.</ListItem>
                </List>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Supervisor</TableCell>
              <TableCell>
                <List type="bullet">
                  <ListItem>
                    Uses summarized information at a shift/line level.
                  </ListItem>
                  <ListItem>Plans with a scope of days to weeks.</ListItem>
                  <ListItem>Most actions are to sustain or improve.</ListItem>
                </List>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Operator</TableCell>
              <TableCell>
                <List type="bullet">
                  <ListItem>
                    Uses detailed and granular information at a local level.
                  </ListItem>
                  <ListItem>Plans with a scope of hours to days.</ListItem>
                  <ListItem>
                    Most actions are to stabilize, prepare, or fix.
                  </ListItem>
                </List>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <BannerAd url={Routes.Vorne.XL.XL} />
        <Heading1 id="keyInsights">Key Insights</Heading1>
        <Heading2>Apply IDA Across Your Business</Heading2>
        <Body1>
          Every level of your business can benefit from IDA (i.e., using
          high-quality information to drive effective actions). The scope and
          nature of each factor changes as it is applied to different levels, as
          shown below.
        </Body1>
        <Table align="L L L L" cellWidths="10 30 30 30">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Level</TableHeaderCell>
              <TableHeaderCell>Information</TableHeaderCell>
              <TableHeaderCell>Decision</TableHeaderCell>
              <TableHeaderCell>Action</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell>Strategic</TableCell>
              <TableCell>
                Broad and varied information, carefully formatted to save time.
                Often trended and aggregated.
              </TableCell>
              <TableCell>
                Periodic reviews that assess trends and develop new large-scale
                initiatives.
              </TableCell>
              <TableCell>
                Longer-term actions that reinforce vision, manage change, and
                dramatically improve the business.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Tactical</TableCell>
              <TableCell>
                Mixed information that crosses levels and provides early
                warnings of potential problems.
              </TableCell>
              <TableCell>
                Regular reviews that audit and improve existing processes.
              </TableCell>
              <TableCell>
                Medium-term actions that address escalated issues and generate
                proactive improvements.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Operational</TableCell>
              <TableCell>
                Granular real-time information that encourages proactive
                responses to changing conditions.
              </TableCell>
              <TableCell>
                Frequent reviews to identify incremental course change
                adjustments.
              </TableCell>
              <TableCell>
                Shorter-term actions to fix problems, prepare for challenges and
                “win the day”.
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <Heading2>Tips on IDA: Information</Heading2>
        <Heading3>Prefer Quality Over Quantity</Heading3>
        <Body1>
          It's relatively easy to capture information, but much harder to ensure
          that captured information is accurate and actionable. As a result,
          many companies capture reams of information, much of which is not used
          or useful. Instead, measure only what you need to make effective
          decisions now. Audit and improve accuracy. Eliminate everything else.
        </Body1>
        <Heading3>Get it Right at the Source</Heading3>
        <Body1>
          The majority of manufacturing information comes directly from the
          factory floor. Put processes in place to ensure that data is
          accurately collected and automate data capture wherever possible.
        </Body1>
        <Heading2>Tips on IDA: Decision</Heading2>
        <Heading3>Run Great Meetings</Heading3>
        <Body1>
          The key to effective decisions are{" "}
          <Link url={Routes.Vorne.GreatMeetings}>Great Meetings</Link>. Great
          meetings are standardized, positive, energetic, action-oriented,
          idea-focused, and forward-focused. Define what great meetings look
          like for your organization, then train and coach teams to sustain this
          standard.
        </Body1>
        <Heading3>Align Across Levels</Heading3>
        <Body1>
          There is an art to performing actions in a congruent way across a
          business. A highly effective tool for this is{" "}
          <Link url={Routes.Vorne.StructuredImprovement}>
            Structured Improvement
          </Link>
          , which aligns meetings across a business in a way that coordinates
          actions and sets clear scope boundaries (avoiding duplication of
          work).
        </Body1>
        <Center $horizontally>
          {" "}
          <Image
            src={data.structuredImprovementMeetingImage}
            alt="Table showing that in the Decision stage of IDA, you should take operational, tactical, and strategic perspectives into account."
            renderCaption={() => (
              <Fragment>
                Regularly scheduled meetings are the perfect place to
                consistently apply Decision across your business. Standardize
                them through{" "}
                <Link url={Routes.Vorne.StructuredImprovement}>
                  Structured Improvement
                </Link>
                .
              </Fragment>
            )}
          />
        </Center>

        <Heading2>Tips on IDA: Action</Heading2>
        <Heading3>Use the Best Tool for the Job</Heading3>
        <Body1>
          There are many manufacturing improvement tools available through Lean
          Manufacturing, Theory of Constraints, Six Sigma, etc. Pick the tool
          that's most appropriate based on the available Information.
        </Body1>
        <Heading3>Get it Done</Heading3>
        <Body1>
          Once an action has been identified, complete it fully or escalate it
          to a higher level. Prefer small and frequent actions in the spirit of{" "}
          <Link url={Routes.Vorne.Agile}>Agile</Link>.
        </Body1>
        <Heading1 id="levelAndDifficulty">Level and Difficulty</Heading1>
        <Body1>
          <strong>The Level is Foundation.</strong> IDA can dramatically improve
          results by focusing on the factors that truly influence those results.
        </Body1>
        <Body1>
          <strong>The Difficulty is Easy.</strong> The concepts of IDA are
          straightforward and easy to explain at any level of the business.
        </Body1>
        <Heading1 id="rateYourIdaFramework">Rate Your Ida Framework</Heading1>
        <Body1>
          How good is your site at IDA? Answer ten simple questions to see how
          close you are to a model implementation.
        </Body1>
        <Table align="L L" cellWidths="95 5">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Question</TableHeaderCell>
              <TableHeaderCell>✔</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell>
                <strong>1.</strong> Are all employees trained on IDA?
              </TableCell>
              <TableCell></TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <strong>2.</strong> Is Information accurate, relevant, and
                easy-to-understand?
              </TableCell>
              <TableCell></TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <strong>3.</strong> Is Information consistently utilized by all
                employees?
              </TableCell>
              <TableCell></TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <strong>4.</strong> Is quality of Information preferred over
                quantity of Information?
              </TableCell>
              <TableCell></TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <strong>5.</strong> Are Decision meetings used to transform
                Information into Actions?
              </TableCell>
              <TableCell></TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <strong>6.</strong> Are Actions assigned to individuals and
                verified for completion?
              </TableCell>
              <TableCell></TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <strong>7.</strong> Are Information, Decision, and Action
                well-balanced?
              </TableCell>
              <TableCell></TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <strong>8.</strong> Has the weakest link in the IDA chain been
                identified?
              </TableCell>
              <TableCell></TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <strong>9.</strong> Are inputs (IDA) emphasized over outputs
                (Results)?
              </TableCell>
              <TableCell></TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <strong>10.</strong> Are objectives tied back to Information,
                Decision, and Actions?
              </TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <Heading1 id="commentsOrQuestions">Comments or Questions?</Heading1>
        <Body1>
          We welcome your comments and questions. Contact us at:{" "}
          <Link url={Routes.Vorne.Contact.InfoEmail}>info@vorne.com</Link>.
        </Body1>
      </Section>
      <Script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: `{
                      "@context": "https://schema.org/",
                      "@type": "TechArticle",
                      "proficiencyLevel": "beginner",
                      "headline": "Ida (Information, Decision, Action)",
                      "name": "Ida (Information, Decision, Action)",
                      "description": "Learn how to employ the IDA framework to consistently achieve better results.",
                      "url": "https://www.vorne.com/learn/accelerants/information-decision-action/",
                      "mainEntityOfPage": "https://www.vorne.com/learn/accelerants/information-decision-action/",
                      "publisher": { "@type": "Organization", "@id": "#vorneorg" },
                      "author": { "@type": "Organization", "@id": "#vorneorg" },
                      "image": {
                          "url": "https://www.vorne.com/static/31610b77aff1e70eb76e48cb3f4fbc01/35ec5/ida-equation-image.webp",
                          "width": "1576",
                          "height": "322"
                      }
                  }`,
        }}
      />
    </Page>
  );
};

export default InformationDecisionAction;

export function Head() {
  return (
    <Seo
      title="The IDA Model: Information, Decision, Action"
      description="Learn how to employ the IDA (Information, Decision, Action) framework to consistently achieve better manufacturing results."
    />
  );
}

export const query = graphql`
  query {
    idaEquationImage: file(relativePath: { eq: "ida-equation-image.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED)
      }
    }
    idaOeeInformationImage: file(
      relativePath: { eq: "ida-oee-information-image.png" }
    ) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED)
      }
    }
    idaOeeDecisionImage: file(
      relativePath: { eq: "ida-oee-decision-image.png" }
    ) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, width: 900)
      }
    }
    idaOeeActionImage: file(relativePath: { eq: "ida-oee-action-image.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED)
      }
    }
    structuredImprovementMeetingImage: file(
      relativePath: { eq: "structured-improvement-meeting-image.png" }
    ) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, width: 600)
      }
    }
  }
`;
